
import axios from '../../helpers/bearerAxios';
import { skipPartnerErrorPopupConfig } from '../../helpers/partnerApiErrorInterceptor';
import { normalizeTitleContactUpdate } from '../../helpers/titleSearchOwners';
import { logger } from '../loggingService';

const baseUrl = import.meta.env.VITE_ONEBUTTON_OPPORTUNITY_API_URL || '';
const baseUrlNonV2 = import.meta.env.VITE_ONEBUTTON_OPPORTUNITY_API_URL_NON_V2 || '';
const blackKnightApiUrl = import.meta.env.VITE_BLACK_KNIGHT_API_URL || '';
const contractBaseUrl = import.meta.env.VITE_ONEBUTTON_CONTRACT_BASE_URL || baseUrl.replace(/\/api\/Contract.*$/i, '') || '';
/** Same host as Angular `environment.opportunityApiUrl` (`.../api/Opportunity/...`) */
const opportunityLegacyBase =
    import.meta.env.VITE_ONEBUTTON_OPPORTUNITY_BASE_URL ||
    String(baseUrlNonV2 || '')
        .trim()
        .replace(/\/api\/opportunity\/?$/i, '')
        .replace(/\/api\/v2\/opportunity\/?$/i, '');

/** Raised when OpportunityDetail returns 400 (lead / opportunity not in oneBUTTON). */
export class OpportunityDetailNotFoundError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'OpportunityDetailNotFoundError';
    }
}

function opportunityDetailNotFoundMessage(responseData: unknown, fallback: string): string {
    if (responseData && typeof responseData === 'object') {
        const row = responseData as Record<string, unknown>;
        const msg = row.Message ?? row.message;
        if (typeof msg === 'string' && msg.trim()) return msg.trim();
    }
    if (typeof responseData === 'string' && responseData.trim()) return responseData.trim();
    return fallback;
}

/**
 * Trinity `opportunity-info.service.ts` — TPO rule validation (e.g. GoodLeap multiplier on Quote page).
 * GET `.../api/Opportunity/TPORuleValidation?opportunityId=&pageName=&financePartnerId=`
 */
const tpoRuleValidation = async (opportunityId: string | number, pageName: string, financePartnerId: string | number) => {
    const root = String(opportunityLegacyBase || '').replace(/\/$/, '');
    if (!root) {
        logger.warn('Opportunity base URL not configured for TPORuleValidation');
        throw new Error('Opportunity API base URL is not configured.');
    }
    try {
        const url = `${root}/api/Opportunity/TPORuleValidation?opportunityId=${encodeURIComponent(String(opportunityId))}&pageName=${encodeURIComponent(pageName)}&financePartnerId=${encodeURIComponent(String(financePartnerId))}`;
        const response = await axios.get(url);
        return response?.data ?? response;
    } catch (error: any) {
        logger.error(error);
        throw error;
    }
};

/**
 * Fetch opportunity details by Salesforce ID
 * @param salesForceId The Salesforce ID of the opportunity
 * @returns Opportunity details response, or null on unexpected errors
 * @throws {@link OpportunityDetailNotFoundError} when the API returns 400 (no lead/opportunity record)
 */
const getOpportunityDetail = async (salesForceId: string): Promise<any | null> => {
    if (!baseUrl?.trim()) {
        logger.warn('Opportunity API base URL not configured');
        return null;
    }
    try {
        const url = `${baseUrl}/OpportunityDetail/${salesForceId}`;
        const response = await axios.get(url);
        return response.data;
    } catch (error: any) {
        const status = error?.response?.status;
        const responseData = error?.response?.data;
        const message =
            responseData?.message ??
            (typeof responseData === 'string' ? responseData : null) ??
            error?.message;

        // Expected "no data" case: this endpoint can return 400 for missing lead/opportunity details.
        if (status === 400) {
            throw new OpportunityDetailNotFoundError(
                opportunityDetailNotFoundMessage(
                    responseData,
                    'No records found for lead details',
                ),
            );
        }

        logger.error(`getOpportunityDetail failed (${status}): ${message}`, error);
        return null;
    }
};

/**
 * Updates contact information for an opportunity
 * @param payload The contact information payload
 * @returns Success response
 */
const updateContactInformation = async (payload: any) => {
    try {        
        const url = `${baseUrl}/UpdateContactInformation`;
        
        const response = await axios.post(url, payload);
        return response.data;
    } catch (error) {
        logger.error(error);
        throw error;
    }
};

const removeContact = async (contactId: string) => {
    try {       
        const url = `${baseUrlNonV2}/RemoveContact?contactId=${contactId}`;
        
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        logger.error(error);
        throw error;
    }
};

/**
 * Fetches customer title and property information using Black Knight API
 * @param directLeadId The direct lead or opportunity ID
 * @param installationAddress The installation address object containing Street, ZipCode, Latitude, Longitude
 * @returns Property title information
 */
const getCustomerTitleInfo = async (directLeadId: any, installationAddress: any) => {
    try {
        const street = installationAddress?.Street || '';
        const zipCode = (installationAddress?.ZipCode || installationAddress?.zipCode || '').slice(0, 5);
        const latitude = installationAddress?.Latitude || '';
        const longitude = installationAddress?.Longitude || '';
        
        const url = `${blackKnightApiUrl}/CustomerTitleInfo?directLeadId=${directLeadId}&street=${street}&zipCode=${zipCode}&latitude=${latitude}&longitude=${longitude}`;
        
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        logger.error(error);
        throw error;
    }
};


/**
 * Fetch existing leads from Finance Partners for an opportunity.
 * @param opportunityId Opportunity ID
 * @returns Leads data
 */
const getFinancePartnerLeads = async (opportunityId: string) => {
    try {
        const url = `${baseUrlNonV2}/FinancePartnerLeadSearch?opportunityId=${encodeURIComponent(opportunityId)}`;
        const response = await axios.get(url);
        return response?.data ?? response;
    } catch (error: any) {
        logger.error(error);
        throw error;
    }
};

const getCustomerTitleInfooneLead = async (directLeadId: string, street: string, zipCode: string, latitude: string, longitude: string) => {
    try {
        const params = new URLSearchParams({
            directLeadId: String(directLeadId ?? ''),
            street: String(street ?? ''),
            zipCode: String(zipCode ?? ''),
            latitude: String(latitude ?? ''),
            longitude: String(longitude ?? ''),
        });
        const url = `${baseUrlNonV2}/GetCustomerTitleInfooneLead?${params.toString()}`;
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        logger.error(error);
        throw error;
    }
};

const opportunityContactTitleUpdate = async (payload: { opportunityId: string; contacts: any[] }) => {
    try {
        const contacts = (payload.contacts ?? []).map((contact) =>
            normalizeTitleContactUpdate(contact),
        );
        const url = `${baseUrlNonV2}/OpportunityContactTitleUpdate?opportunityId=${payload.opportunityId}`;
        const response = await axios.post(url, contacts);
        return response.data;
    } catch (error) {
        logger.error(error);
        throw error;
    }
};

/** Save opportunity product rows (finance partner + method per product). POST /api/v2/opportunity/UpdateOpportunityProduct */
const updateOpportunityProduct = async (payload: {
    opportunityId: number;
    salesForce_Id: string;
    opportunityProduct: Array<{
        opportunity_Id: number;
        product_Type_Name: string;
        product_Type_Id: number;
        finance_Partner_Id: number;
        activeFlag: boolean;
        opportunity_Product_Id: number;
        finance_Partner: string;
        finance_Partner_Display_Name: string;
        finance_Method_Name: string;
    }>;
}) => {
    if (!baseUrl?.trim()) {
        logger.warn('Opportunity API base URL not configured');
        throw new Error('Opportunity API not configured');
    }
    try {
        const url = `${baseUrl.replace(/\/$/, '')}/UpdateOpportunityProduct`;
        const response = await axios.post(url, payload);
        return response?.data ?? response;
    } catch (error) {
        logger.error(error);
        throw error;
    }
};

/**
 * Retrieve the status and results of display contacts for contract generation.
 */
const displayContacts = async (opportunityId: string) => {
    try {
        const url = `${contractBaseUrl}/api/Contract/DisplayContacts?opportunityId=${encodeURIComponent(opportunityId)}`;
        const response = await axios.get(url);
        return response?.data ?? response;
    } catch (error: any) {
        logger.error(error);
        throw error;
    }
};

/**
 * Electronic consent for IGS credit check.
 */
const ElectronicConsent = async (leadId: string) => {
    try {
        const url = `${contractBaseUrl}/api/Contract/ElectronicConsent?leadId=${encodeURIComponent(leadId)}`;
        const response = await axios.get(url);
        return response?.data ?? response;
    } catch (error: any) {
        logger.error(error);
        throw error;
    }
};

/**
 * Get credit enquiry invitation for IGS credit check.
 */
const getCreditEnquiryInvitation = async (leadId: string) => {
    try {
        const url = `${contractBaseUrl}/api/Contract/GetCreditEnquiryInvitation?leadId=${encodeURIComponent(leadId)}`;
        const response = await axios.get(url);
        return response?.data ?? response;
    } catch (error: any) {
        logger.error(error);
        throw error;
    }
};

/**
 * Check credit for Lightreach and Sunlight partners.
 */
const checkCreditFinancePartner = async (data: any) => {
    try {
        const url = `${contractBaseUrl}/api/Contract/CheckCreditFinancePartner`;
        const response = await axios.post(url, data);
        return response?.data ?? response;
    } catch (error: any) {
        logger.error(error);
        throw error;
    }
};

/**
 * Check credit for Sunnova (Angular `GenerateQuoteService.checkCreditSunnova`).
 * POST body: `{ ContactId, Action: 'inperson'|'email', opportunityId }`
 * Response may be a JSON string with `signingURL` or a plain error string.
 */
const checkCreditSunnova = async (data: {
    ContactId: string | number | null | undefined;
    Action: string;
    opportunityId: string | number;
}) => {
    try {
        const url = `${contractBaseUrl}/api/Contract/CheckCreditSunnova`;
        const response = await axios.post(url, data);
        return response?.data ?? response;
    } catch (error: any) {
        logger.error(error);
        throw error;
    }
};

/**
 * Fetch Sunlight Credit Status
 */
const getSunlightCreditStatus = async (opportunityId: string | number) => {
    if (!contractBaseUrl?.trim()) {
        return null;
    }
    try {
        const url = `${contractBaseUrl}/api/Contract/GetSunlightCreditStatus?opportunityId=${encodeURIComponent(String(opportunityId))}`;
        const response = await axios.get(url, skipPartnerErrorPopupConfig);
        return response?.data ?? response;
    } catch (error: any) {
        const status = error?.response?.status;
        // Endpoint may be unavailable in some environments (e.g. dev).
        if (status === 404 || status === 400) {
            return null;
        }
        logger.error(error);
        throw error;
    }
};

/**
 * Get Black Knight Synced
 */
const getIsBlackKnightSync = async (opportunityId: string) => {
    try {
        const url = `${baseUrlNonV2}/GetBlackKnightSynced?OpportunityId=${encodeURIComponent(opportunityId)}`;
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        logger.error(error);
        throw error;
    }
};

/**
 * Retrieve Black Knight Property Search
 */
const RetrieveBlackKnightPropertySearch = async (payload: any) => {
    try {
        const url = `${baseUrlNonV2}/BlackKnightPropertySearch`;
        const response = await axios.post(url, payload);
        return response.data;
    } catch (error) {
        logger.error(error);
        throw error;
    }
};

/**
 * Retrieve Google Street View
 */
const getGoogleStreet = async (latitude: number | string, longitude: number | string) => {
    try {
        const url = `${baseUrlNonV2}/GoogleStreet?lat=${latitude}&lon=${longitude}`;
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        logger.error(error);
        throw error;
    }
};

/**
 * Retrieve opportunity details by Opportunity ID
 */
const retrieveOpportunityDetails = async (opportunityId: string | number) => {
    try {
        const roleName = typeof window !== 'undefined' ? sessionStorage.getItem('userRole') || 'Admin' : 'Admin';
        const url = `${baseUrlNonV2}/RetrieveOpportunityDetails?opportunityId=${encodeURIComponent(String(opportunityId))}&roleName=${encodeURIComponent(roleName)}&ownerId=`;
        const response = await axios.get(url);
        return response?.data ?? response;
    } catch (error: any) {
        logger.error(error);
        throw error;
    }
};

/**
 * Trinity `opportunity-info.service.ts` `retrieveSalesRepChecklistData`.
 * GET `.../api/Opportunity/GetChecklist?opportunityId=`
 */
const retrieveSalesRepChecklistData = async (opportunityId: string | number) => {
    const root = String(opportunityLegacyBase || '').replace(/\/$/, '');
    if (!root) {
        logger.warn('Opportunity base URL not configured for GetChecklist');
        throw new Error('Opportunity API base URL is not configured.');
    }
    try {
        const url = `${root}/api/Opportunity/GetChecklist?opportunityId=${encodeURIComponent(String(opportunityId))}`;
        const response = await axios.get(url);
        return response?.data ?? response;
    } catch (error) {
        logger.error(error);
        throw error;
    }
};

/**
 * Trinity `opportunity-info.service.ts` `SaveChecklistResponses`.
 * POST `.../api/Opportunity/SaveChecklistResponses`
 */
const saveChecklistResponses = async (payload: {
    opportunityId: string | number;
    answers: unknown[];
    allActiveQuestionIds: unknown[];
}) => {
    const root = String(opportunityLegacyBase || '').replace(/\/$/, '');
    if (!root) {
        logger.warn('Opportunity base URL not configured for SaveChecklistResponses');
        throw new Error('Opportunity API base URL is not configured.');
    }
    try {
        const url = `${root}/api/Opportunity/SaveChecklistResponses`;
        const response = await axios.post(url, payload);
        return response?.data ?? response;
    } catch (error) {
        logger.error(error);
        throw error;
    }
};

/**
 * Trinity `opportunity-info.service.ts` `retrieveAuditTrailData`.
 * GET `.../api/Opportunity/GetChecklistResponseAudit?opportunityId=`
 */
const retrieveChecklistAuditTrail = async (opportunityId: string | number) => {
    const root = String(opportunityLegacyBase || '').replace(/\/$/, '');
    if (!root) {
        logger.warn('Opportunity base URL not configured for GetChecklistResponseAudit');
        throw new Error('Opportunity API base URL is not configured.');
    }
    try {
        const url = `${root}/api/Opportunity/GetChecklistResponseAudit?opportunityId=${encodeURIComponent(String(opportunityId))}`;
        const response = await axios.get(url);
        return response?.data ?? response;
    } catch (error) {
        logger.error(error);
        throw error;
    }
};

/**
 * Trinity `opportunity-info.service.ts` `getChecklistResponseTone`.
 * GET `.../api/Opportunity/GetChecklistResponseTone?opportunityId=`
 */
const getChecklistResponseTone = async (opportunityId: string | number) => {
    const root = String(opportunityLegacyBase || '').replace(/\/$/, '');
    if (!root) {
        logger.warn('Opportunity base URL not configured for GetChecklistResponseTone');
        throw new Error('Opportunity API base URL is not configured.');
    }
    try {
        const url = `${root}/api/Opportunity/GetChecklistResponseTone?opportunityId=${encodeURIComponent(String(opportunityId))}`;
        const response = await axios.get(url);
        return response?.data ?? response;
    } catch (error) {
        logger.error(error);
        throw error;
    }
};

const opportunityInfoServices = {
    getOpportunityDetail,
    tpoRuleValidation,
    updateContactInformation,
    removeContact,
    getCustomerTitleInfo,
    getCustomerTitleInfooneLead,
    opportunityContactTitleUpdate,
    getFinancePartnerLeads,
    updateOpportunityProduct,
    displayContacts,
    ElectronicConsent,
    getCreditEnquiryInvitation,
    checkCreditFinancePartner,
    checkCreditSunnova,
    getSunlightCreditStatus,
    getIsBlackKnightSync,
    RetrieveBlackKnightPropertySearch,
    getGoogleStreet,
    retrieveOpportunityDetails,
    retrieveSalesRepChecklistData,
    saveChecklistResponses,
    retrieveChecklistAuditTrail,
    getChecklistResponseTone,
};

export default opportunityInfoServices;